import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseRepository } from '../Common/Data/base.repository';
import {
    BulkInvoicePreCheckResult,
    CreateAdHocInvoiceRequest,
    Invoice,
} from '../Processing/Invoice/invoice.model';

@Injectable({ providedIn: 'root' })
export class InvoiceRepository extends BaseRepository {
    constructor(
        httpClient: HttpClient
    ) {
        super(httpClient);
    }

    getInvoiceByTaskIds(taskIds: number[]): Observable<any> {
        return this.httpPost('/api/invoice', taskIds);
    }

    bulkMarkUBR(taskIds: number[]): Observable<any> {
        return this.httpPost('/api/invoice/bulkmarkubr', taskIds);
    }

    bulkTransferToRIBS(taskIds: number[]): Observable<any> {
        return this.httpPost('/api/invoice/bulktransfer', taskIds);
    }

    getInvoice(invoiceId: string): Observable<Invoice> {
        return this.httpGet<Invoice>(`/api/Invoice/${invoiceId}`);
    }

    getInvoiceForTask(taskId: number): Observable<Invoice> {
        return this.httpGet<Invoice>(`/api/Invoice/Task/${taskId}`);
    }

    searchInvoiceAppeals(
        invoiceId: number,
        appealIds: number[],
        generateTaxSavingsWorksheet: boolean,
    ): Observable<Core.AppealSearchResult> {
        return this.httpPost<Core.AppealSearchResult, number[]>(
            `/api/Invoice/${invoiceId}/appeal/search?generateTaxSavingsWorksheet=${generateTaxSavingsWorksheet}`,
            appealIds,
        );
    }

    saveInvoice(invoice: Invoice): Observable<Invoice> {
        return this.httpPut<Invoice, Invoice>(`/api/Invoice/${invoice.invoiceId}`, invoice);
    }

    completeInvoice(invoice: Invoice): Observable<Invoice> {
        return this.httpPut<Invoice, Invoice>(`/api/Invoice/${invoice.invoiceId}/readytask`, invoice, { handleCode: 400 });
    }

    deleteInvoice(invoiceId: number): Observable<void> {
        return this.httpDelete<void>(`/api/invoice/${invoiceId}`);
    }

    getSiteInvoices(siteId: number, dateRange: { fromDate: Date; toDate: Date }): Observable<Invoice[]> {
        return this.httpPost<Invoice[], { fromDate: Date; toDate: Date }>(`/api/site/${siteId}/invoices`, dateRange);
    }

    getCompanyInvoices(companyId: number, dateRange: { fromDate: Date; toDate: Date }): Observable<Invoice[]> {
        return this.httpPost<Invoice[], { fromDate: Date; toDate: Date }>(`/api/company/${companyId}/invoices`, dateRange);
    }

    getReadyInvoiceAppealSavingTasksCount(siteId: number): Observable<number> {
        return this.httpGet<number>(`/api/site/${siteId}/getParcelsReadyForInvoiceCount`);
    }

    getReadyAdminFeeInvoiceCount(companyId: number): Observable<number> {
        return this.httpGet<number>(`/api/company/${companyId}/getReadyAdminFeeInvoiceCount`);
    }

    bulkInvoicePreCheck(companyId: number, annualYear: number): Observable<BulkInvoicePreCheckResult> {
        return this.httpGet<BulkInvoicePreCheckResult>(`/api/company/${companyId}/year/${annualYear}/bulkInvoicePreCheck`);
    }

    createInvoices(siteId: number): Observable<void> {
        return this.httpPost<void, null>(`/api/invoice/${siteId}/createInvoices`, null);
    }

    createCompanyAdminInvoices(companyId: number, annualYear: number): Observable<void> {
        return this.httpPost<void, { companyId: number; annualYear: number }>('/api/invoice/adminbulk', {
            companyId,
            annualYear,
        });
    }

    getCompanyInvoiceDefaults(companyId: number): Observable<CreateAdHocInvoiceRequest> {
        return this.httpGet<CreateAdHocInvoiceRequest>(`/api/invoice/company/${companyId}/adhocdefaults`);
    }

    getSiteInvoiceDefaults(siteId: number): Observable<CreateAdHocInvoiceRequest> {
        return this.httpGet<CreateAdHocInvoiceRequest>(`/api/invoice/site/${siteId}/adhocdefaults`);
    }

    createAdHocInvoice(request: CreateAdHocInvoiceRequest): Observable<number> {
        return this.httpPost<number, CreateAdHocInvoiceRequest>('/api/invoice/adhoc', request);
    }

    generateWorksheet(invoiceId: number, includeOtherParcels: boolean): Observable<void> {
        return this.httpGet<void>(
            `/api/invoice/${invoiceId}/generateWorksheet?includeOtherParcels=${includeOtherParcels}`,
        );
    }

    deleteTaxSavingsWorksheets(invoiceId: number): Observable<boolean> {
        return this.httpDelete<boolean>(`/api/invoice/${invoiceId}/deleteTaxSavingsWorksheets`);
    }
}
